import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";

import { UrlConstantes } from "../utilerias/constantes/urlconstantes";
import {
  DictamenEval,
  EvalProveedor,
  EvalProveedorReq,
  EvalProveedorRes,
  RespuestaEval,
} from "../shared/model/iso42001/evaluacion-proveedor.model";

/**
 * Evaluaciones IA Solser.
 *
 * Los ARCHIVOS no viven aquí: se suben, se bajan y se quitan con el
 * almacén genérico de evidencias ISO 42001 (MatrizRiesgoService), con
 * tipoOrigen EVAL_PROVEEDOR_DOC e idOrigen = idEvalDoc. Es el mismo que
 * usan Solicitudes de IA y el monitoreo del inventario.
 */
@Injectable({ providedIn: "root" })
export class EvaluacionesIaService {
  constructor(private http: HttpClient) {}

  /** Los expedientes, cada uno con su resultado, y el catálogo de preguntas. */
  lista(): Observable<EvalProveedorRes> {
    return this.http.post<EvalProveedorRes>(UrlConstantes.EVALUACIONES_IA_LISTA, {});
  }

  /** Un expediente completo: respuestas, documentos con archivos y resultado. */
  detalle(idEvalProveedor: number): Observable<EvalProveedorRes> {
    const req: EvalProveedorReq = { idEvalProveedor };
    return this.http.post<EvalProveedorRes>(UrlConstantes.EVALUACIONES_IA_DETALLE, req);
  }

  historial(idEvalProveedor: number): Observable<EvalProveedorRes> {
    const req: EvalProveedorReq = { idEvalProveedor };
    return this.http.post<EvalProveedorRes>(UrlConstantes.EVALUACIONES_IA_HISTORIAL, req);
  }

  crear(nombre: string, casoUso: string): Observable<EvalProveedorRes> {
    const req: EvalProveedorReq = { evaluacion: { nombre, casoUso } };
    return this.http.post<EvalProveedorRes>(UrlConstantes.EVALUACIONES_IA_CREAR, req);
  }

  /**
   * Razón social, servicio, plan y fecha van siempre: el que llegue vacío se
   * borra. Nombre y caso de uso sólo cuando se editan desde la tabla.
   */
  guardarDatos(
    idEvalProveedor: number,
    datos: Pick<EvalProveedor, "razonSocial" | "servicio" | "planModalidad" | "fechaEvaluacion"> &
      Partial<Pick<EvalProveedor, "nombre" | "casoUso">>,
  ): Observable<EvalProveedorRes> {
    const req: EvalProveedorReq = { idEvalProveedor, evaluacion: datos };
    return this.http.post<EvalProveedorRes>(UrlConstantes.EVALUACIONES_IA_DATOS, req);
  }

  /** Devuelve el expediente recalculado. */
  responder(idEvalProveedor: number, clave: string, respuesta: RespuestaEval | null): Observable<EvalProveedorRes> {
    const req: EvalProveedorReq = { idEvalProveedor, clave, respuesta };
    return this.http.post<EvalProveedorRes>(UrlConstantes.EVALUACIONES_IA_RESPONDER, req);
  }

  /** "Limpiar cuestionario": borra todas las respuestas; observaciones y archivos se quedan. */
  limpiarCuestionario(idEvalProveedor: number): Observable<EvalProveedorRes> {
    const req: EvalProveedorReq = { idEvalProveedor };
    return this.http.post<EvalProveedorRes>(UrlConstantes.EVALUACIONES_IA_LIMPIAR, req);
  }

  observar(idEvalProveedor: number, clave: string, observacion: string): Observable<EvalProveedorRes> {
    const req: EvalProveedorReq = { idEvalProveedor, clave, observacion };
    return this.http.post<EvalProveedorRes>(UrlConstantes.EVALUACIONES_IA_OBSERVACION, req);
  }

  referencia(idEvalProveedor: number, claveDoc: string, referencia: string): Observable<EvalProveedorRes> {
    const req: EvalProveedorReq = { idEvalProveedor, claveDoc, referencia };
    return this.http.post<EvalProveedorRes>(UrlConstantes.EVALUACIONES_IA_REFERENCIA, req);
  }

  firmar(idEvalProveedor: number, dictamen: DictamenEval, firma: string): Observable<EvalProveedorRes> {
    const req: EvalProveedorReq = { idEvalProveedor, dictamen, firma };
    return this.http.post<EvalProveedorRes>(UrlConstantes.EVALUACIONES_IA_FIRMAR, req);
  }

  reabrir(idEvalProveedor: number, motivo: string): Observable<EvalProveedorRes> {
    const req: EvalProveedorReq = { idEvalProveedor, motivo };
    return this.http.post<EvalProveedorRes>(UrlConstantes.EVALUACIONES_IA_REABRIR, req);
  }

  /** Elimina un expediente abierto creado por error. El PIN lo valida el servidor. */
  eliminar(idEvalProveedor: number, pin: string): Observable<EvalProveedorRes> {
    const req: EvalProveedorReq = { idEvalProveedor, pin };
    return this.http.post<EvalProveedorRes>(UrlConstantes.EVALUACIONES_IA_ELIMINAR, req);
  }
}
